/**
 * Single orchestrator for collection/search grid updates:
 * filters, quick-filters, sort, and pagination.
 */
(function () {
  const MOBILE_QUERY = '(max-width: 749px)';
  const GRID_SELECTOR = '#ResultsList .product-grid, ul#product-grid.product-grid';
  const AJAX_PARAM = 'ajax';
  const AJAX_VALUE = '1';
  const AJAX_VIEW = 'ajax';
  const AJAX_SECTION_ID = 'collection-grid-ajax';
  const CACHE_MAX = 24;
  const GRID_ATTRS = [
    'data-current-page',
    'data-last-page',
    'data-total-pages',
    'data-page-size',
    'data-total-items',
    'data-view',
  ];

  class CollectionController {
    static instance = null;

    constructor() {
      this.abortController = null;
      this.isNavigating = false;
      this.urlPrev = window.location.pathname + window.location.search;
      this.gridContainer = document.getElementById('ProductGridContainer');
      this.responseCache = new Map();
      this.inFlightFetches = new Map();
    }

    static init() {
      if (!document.getElementById('product-grid') && !document.getElementById('ProductGridContainer')) {
        return null;
      }

      // Infinite scroll / Show more: if someone refreshes on ?page=2+, Shopify only
      // renders that page's products. Bounce back to page 1 (keep filters/sort).
      CollectionController.resetDeepPaginationIfNeeded();

      if (!CollectionController.instance) {
        CollectionController.instance = new CollectionController();
        CollectionController.instance.bind();
      }
      return CollectionController.instance;
    }

    static resetDeepPaginationIfNeeded() {
      try {
        const params = new URLSearchParams(window.location.search);
        const page = parseInt(params.get('page') || '1', 10);
        if (!(page > 1)) return;

        const hasInfinite = Boolean(document.querySelector('infinite-scrolling[data-next-url], infinite-scrolling'));
        if (!hasInfinite) return;

        params.delete('page');
        const clean =
          window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
        if (clean === window.location.pathname + window.location.search + window.location.hash) return;
        window.location.replace(clean);
      } catch (e) {
        // Ignore URL cleanup errors.
      }
    }

    getSectionId() {
      return (
        document.getElementById('product-grid')?.dataset?.id ||
        document.querySelector('#ProductGridContainer .collection[data-id]')?.getAttribute('data-id') ||
        document.querySelector('.collection[data-id]')?.getAttribute('data-id') ||
        document.querySelector('infinite-scrolling[data-section-id]')?.dataset?.sectionId ||
        document.querySelector('[data-section-id]')?.getAttribute('data-section-id') ||
        ''
      );
    }

    getGrid() {
      return document.querySelector(GRID_SELECTOR);
    }

    getCollectionRoot() {
      return document.querySelector('#ProductGridContainer .collection');
    }

    isPlpContainer(container) {
      container = container || document.getElementById('ProductGridContainer');
      return Boolean(container?.querySelector('[data-plp-card]'));
    }

    resolveNavigationUrl(url) {
      const target = new URL(url, window.location.origin);
      const currentSort = new URLSearchParams(window.location.search).get('sort_by');
      if (currentSort && !target.searchParams.has('sort_by')) {
        target.searchParams.set('sort_by', currentSort);
      }
      return target.pathname + target.search;
    }

    normalizeUrl(url) {
      const absoluteUrl = new URL(url, window.location.origin);
      absoluteUrl.searchParams.delete(AJAX_PARAM);
      absoluteUrl.searchParams.delete('view');
      absoluteUrl.searchParams.delete('section_id');
      absoluteUrl.searchParams.delete('sections');
      return absoluteUrl;
    }

    getHistoryUrl(url) {
      const normalized = this.normalizeUrl(this.resolveNavigationUrl(url));
      return normalized.pathname + normalized.search;
    }

    getCacheKey(url) {
      const normalized = this.normalizeUrl(this.resolveNavigationUrl(url));
      return normalized.pathname + normalized.search;
    }

    isLightweightGrid(doc) {
      return Boolean(
        doc.getElementById('CollectionAjaxMeta') ||
        doc.querySelector('.section-collection-grid-ajax')
      );
    }

    showLoading(loading) {
      const collection = this.getCollectionRoot();
      collection?.classList.toggle('loading', loading);

      const grid = this.getGrid();
      grid?.classList.toggle('is-filter-loading', loading);

      document.querySelectorAll('#loader').forEach((loader) => {
        loader.style.display = 'none';
      });
    }

    parseGridDocument(html, parser, { lightweightOnly = false } = {}) {
      if (!html) return null;
      const doc = parser.parseFromString(html, 'text/html');
      if (!doc.getElementById('product-grid') && !doc.getElementById('ProductGridContainer')) {
        return null;
      }
      if (lightweightOnly && !this.isLightweightGrid(doc)) {
        return null;
      }
      return doc;
    }

    async fetchFirstLightweight(paths, signal) {
      if (!paths.length) return null;

      return new Promise((resolve, reject) => {
        let settled = 0;
        const parser = new DOMParser();

        paths.forEach(async (pathWithSearch) => {
          try {
            const html = await this.fetchHtml(pathWithSearch, signal);
            const doc = this.parseGridDocument(html, parser, { lightweightOnly: true });
            if (doc) {
              resolve(doc);
              return;
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              reject(error);
              return;
            }
          }

          settled += 1;
          if (settled === paths.length) {
            resolve(null);
          }
        });
      });
    }

    async fetchHtml(pathWithSearch, signal, headers = {}) {
      const response = await fetch(pathWithSearch, {
        credentials: 'same-origin',
        signal,
        ...headers,
      });
      if (!response.ok) return null;
      return response.text();
    }

    async fetchGridDocument(url, signal) {
      const parser = new DOMParser();
      const baseUrl = this.normalizeUrl(this.resolveNavigationUrl(url));
      const sectionId = this.getSectionId();

      // Prefer ?view=ajax only. Racing section_id=collection-grid-ajax doubles
      // every filter/prefetch request and that section is not on the default
      // collection template, so half the work is wasted.
      const viewUrl = new URL(baseUrl);
      viewUrl.searchParams.set('view', AJAX_VIEW);

      let doc = await this.fetchFirstLightweight(
        [viewUrl.pathname + viewUrl.search],
        signal
      );

      if (!doc && sectionId) {
        const sectionsUrl = new URL(baseUrl);
        sectionsUrl.searchParams.set(AJAX_PARAM, AJAX_VALUE);
        sectionsUrl.searchParams.set('sections', sectionId);

        try {
          const response = await fetch(sectionsUrl.pathname + sectionsUrl.search, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal,
          });
          const contentType = response.headers.get('content-type') || '';

          if (response.ok && contentType.includes('application/json')) {
            const json = await response.json();
            doc = this.parseGridDocument(json[sectionId], parser);
          }
        } catch (error) {
          if (error.name === 'AbortError') throw error;
        }

        if (!doc) {
          const sectionIdUrl = new URL(baseUrl);
          sectionIdUrl.searchParams.set(AJAX_PARAM, AJAX_VALUE);
          sectionIdUrl.searchParams.set('section_id', sectionId);
          doc = this.parseGridDocument(
            await this.fetchHtml(sectionIdUrl.pathname + sectionIdUrl.search, signal),
            parser
          );
        }
      }

      if (!doc) {
        const fullUrl = new URL(baseUrl);
        fullUrl.searchParams.set(AJAX_PARAM, AJAX_VALUE);
        doc = this.parseGridDocument(
          await this.fetchHtml(fullUrl.pathname + fullUrl.search, signal),
          parser
        );
      }

      return doc;
    }

    async fetchDocument(url) {
      const resolvedUrl = this.resolveNavigationUrl(url);
      const cacheKey = this.getCacheKey(resolvedUrl);

      if (this.responseCache.has(cacheKey)) {
        return this.responseCache.get(cacheKey).cloneNode(true);
      }

      let task = this.inFlightFetches.get(cacheKey);
      if (!task) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        task = (async () => {
          const doc = await this.fetchGridDocument(resolvedUrl, signal);
          if (!doc) {
            throw new Error('Collection fetch failed: no product grid in response');
          }

          if (this.responseCache.size >= CACHE_MAX) {
            const oldestKey = this.responseCache.keys().next().value;
            this.responseCache.delete(oldestKey);
          }
          this.responseCache.set(cacheKey, doc);
          return doc.cloneNode(true);
        })();

        this.inFlightFetches.set(cacheKey, task);
        task.finally(() => {
          if (this.inFlightFetches.get(cacheKey) === task) {
            this.inFlightFetches.delete(cacheKey);
          }
        });
      }

      return task;
    }

    prefetch(url) {
      const resolvedUrl = this.resolveNavigationUrl(url);
      const cacheKey = this.getCacheKey(resolvedUrl);
      if (this.responseCache.has(cacheKey) || this.inFlightFetches.has(cacheKey)) return;
      this.fetchDocument(resolvedUrl).catch(() => {});
    }

    prefetchVisibleFilters() {
      const run = () => {
        document
          .querySelectorAll('[data-ajax-filter][href], [data-collection-filter-link][href]')
          .forEach((link) => {
            this.prefetch(link.getAttribute('href'));
          });

        document.querySelectorAll('infinite-scrolling[data-next-url]').forEach((root) => {
          if (root.dataset.nextUrl) {
            this.prefetch(root.dataset.nextUrl);
          }
        });
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 1500 });
      } else {
        setTimeout(run, 400);
      }
    }

    applyDocument(doc, options = {}) {
      const { updateToolbar = false, updateHeader = false } = options;
      const currentGrid = document.getElementById('product-grid');
      const newGrid = doc.getElementById('product-grid');

      if (newGrid && currentGrid) {
        currentGrid.replaceWith(newGrid.cloneNode(true));
      } else {
        const newContainer = doc.getElementById('ProductGridContainer');
        const oldContainer = document.getElementById('ProductGridContainer');
        if (newContainer && oldContainer) {
          oldContainer.innerHTML = newContainer.innerHTML;
        }
      }

      const newPagination = doc.querySelector('.pagination-wrapper');
      const oldPagination = document.querySelector('.pagination-wrapper');
      if (newPagination && oldPagination) {
        oldPagination.innerHTML = newPagination.innerHTML;
      }

      const newCount = doc.querySelector('#CollectionProductCount, #ProductCount');
      const currentCount = document.querySelector('#CollectionProductCount, #ProductCount');
      if (newCount && currentCount) {
        currentCount.innerHTML = newCount.innerHTML;
        currentCount.classList.remove('loading');
      }

      const countMobile = document.getElementById('ProductCountMobile');
      const newCountMobile = doc.getElementById('ProductCountMobile');
      if (newCountMobile && countMobile) {
        countMobile.innerHTML = newCountMobile.innerHTML;
        countMobile.classList.remove('loading');
      }

      document
        .querySelectorAll('.product-count .loading__spinner, .product-grid-container .loading__spinner')
        .forEach((spinner) => spinner.classList.add('hidden'));

      if (updateToolbar) {
        const newSort = doc.querySelector('[data-sorting]');
        const oldSort = document.querySelector('[data-sorting]');
        if (newSort && oldSort) oldSort.replaceWith(newSort.cloneNode(true));

        const newFilters = doc.getElementById('main-collection-filters');
        const oldFilters = document.getElementById('main-collection-filters');
        if (newFilters && oldFilters) oldFilters.innerHTML = newFilters.innerHTML;

        const toolbarEl = document.querySelector('toolbar-item, toolbar');
        if (toolbarEl && typeof toolbarEl.bindEvents === 'function') {
          setTimeout(() => toolbarEl.bindEvents(), 30);
        }
      }

      if (updateHeader) {
        this.updateCollectionChrome(doc);
      }

      this.scheduleReinitGrid();
    }

    updateCollectionChrome(doc) {
      const meta = doc.getElementById('CollectionAjaxMeta');
      if (meta) {
        const title = meta.dataset.title;
        const count = meta.dataset.count;
        if (title) {
          document.title = title;
          const currentTitle = document.querySelector('.collection-header .collection-title');
          if (currentTitle) {
            currentTitle.innerHTML = count
              ? `${title} <span class="collection-count">(${count})</span>`
              : title;
          }
        }
        const collCount = document.getElementById('CollectionProductCount');
        if (collCount && count) {
          collCount.textContent = `(${count})`;
        }
        return;
      }

      const newTitleRow = doc.querySelector('.collection-header__title-row');
      const currentTitleRow = document.querySelector('.collection-header__title-row');
      if (newTitleRow && currentTitleRow) {
        currentTitleRow.innerHTML = newTitleRow.innerHTML;
      }

      const newTitle = doc.querySelector('.collection-header .collection-title');
      if (newTitle) {
        const titleText = newTitle.textContent?.trim();
        if (titleText) {
          document.title = titleText.replace(/\s*\([^)]*\)\s*$/, '').trim();
          const currentTitle = document.querySelector('.collection-header .collection-title');
          if (currentTitle) currentTitle.innerHTML = newTitle.innerHTML;
        }
      }

      if (typeof initCollectionSort === 'function') {
        initCollectionSort(document);
      }
    }

    scheduleReinitGrid() {
      const run = () => this.reinitGrid();

      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 300 });
      } else {
        requestAnimationFrame(run);
      }
    }

    reinitGrid(container) {
      container = container || document.getElementById('ProductGridContainer');
      if (!container) return;

      container.querySelectorAll('.scroll-trigger').forEach((el) => {
        el.classList.add('scroll-trigger--cancel');
      });

      const isPlp = this.isPlpContainer(container);

      if (!isPlp && typeof initMoreSwatchButtons === 'function') {
        initMoreSwatchButtons(container);
      }

      if (!isPlp) {
        container.querySelectorAll('[data-quick-add]').forEach((button) => {
          if (window.QuickAdd?.initButton) {
            window.QuickAdd.initButton(button);
          }
        });
      }

      if (window.lazyLoad?.update) {
        window.lazyLoad.update();
      }

      document.querySelectorAll('infinite-scrolling[data-next-url]').forEach((root) => {
        root.dataset.paginationBound = 'false';
        if (root._paginationObserver) {
          root._paginationObserver.disconnect();
          delete root._paginationObserver;
        }
      });

      document.dispatchEvent(
        new CustomEvent('collection-grid-updated', { detail: { container } })
      );

      this.bindPagination();
    }

    async navigate(url, options = {}) {
      const {
        updateToolbar = false,
        updateHeader = false,
        updateHistory = true,
        replace = false,
        source = 'navigate',
      } = options;

      const historyUrl = this.getHistoryUrl(url);
      const absoluteUrl = new URL(historyUrl, window.location.origin);
      const searchParams = absoluteUrl.search.slice(1);

      const cacheKey = this.getCacheKey(historyUrl);
      const hasCache = this.responseCache.has(cacheKey);
      let loadingTimer = null;

      if (!hasCache) {
        loadingTimer = setTimeout(() => this.showLoading(true), 120);
      }

      try {
        const doc = await this.fetchDocument(historyUrl);
        clearTimeout(loadingTimer);
        this.showLoading(false);
        this.applyDocument(doc, { updateToolbar, updateHeader });

        if (updateHistory) {
          this.isNavigating = true;
          const state = { searchParams, source };
          if (replace) {
            window.history.replaceState(state, '', historyUrl);
          } else {
            window.history.pushState(state, '', historyUrl);
          }
          this.urlPrev = historyUrl;
          requestAnimationFrame(() => {
            this.isNavigating = false;
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[CollectionController]', error);
        }
      } finally {
        clearTimeout(loadingTimer);
        this.showLoading(false);
      }
    }

    onPopState() {
      if (this.isNavigating) return;

      const historyUrl = window.location.pathname + window.location.search;
      if (historyUrl === this.urlPrev) return;

      this.urlPrev = historyUrl;
      this.navigate(historyUrl, {
        updateHistory: false,
        updateToolbar: false,
        updateHeader: true,
        source: 'popstate',
      });
    }

    getProductsFromDocument(doc) {
      if (!doc) return [];
      const container = doc.getElementById('ProductGridContainer');
      if (container) {
        return Array.from(container.querySelectorAll('.product-grid__item'));
      }
      return Array.from(doc.querySelectorAll('.product-grid__item'));
    }

    getExistingProductIds(grid) {
      return new Set(
        Array.from(grid.querySelectorAll('[data-product-id]'))
          .map((el) => el.dataset.productId)
          .filter(Boolean)
      );
    }

    setPaginationLoading(root, loading) {
      const btn = root.querySelector('[data-infinite-scrolling]');
      const grid = this.getGrid();
      if (!btn) return;

      const spinner = btn.querySelector('.loading__spinner');
      const text = btn.querySelector('.text');

      if (loading) {
        btn.classList.add('loading');
        btn.disabled = true;
        spinner?.classList.remove('hidden');
        grid?.classList.add('loading');
      } else {
        btn.classList.remove('loading');
        btn.disabled = false;
        spinner?.classList.add('hidden');
        grid?.classList.remove('loading');
        if (text && window.button_load_more) {
          text.textContent = root.dataset.nextUrl
            ? window.button_load_more.default
            : window.button_load_more.no_more;
        }
      }
    }

    updatePaginationProgress(root, grid) {
      const totalEnd = root.querySelector('[data-total-end]');
      const progressBar = root.querySelector('.pagination-total-item');
      if (!totalEnd || !grid) return;

      const totalItems = parseInt(grid.dataset.totalItems, 10) || 0;
      const count = grid.querySelectorAll('.product-grid__item').length;
      totalEnd.textContent = String(count);

      if (progressBar && totalItems > 0) {
        progressBar.style.width = `${Math.min((count / totalItems) * 100, 100)}%`;
      }
    }

    reinitPaginatedItems(grid, page) {
      const isPlp = this.isPlpContainer();
      const newItems = grid.querySelectorAll(`[data-page="${page}"]`);

      if (!isPlp) {
        newItems.forEach((product) => {
          product.querySelectorAll('[data-quick-add]').forEach((button) => {
            if (window.QuickAdd?.initButton) {
              window.QuickAdd.initButton(button);
            }
          });
        });

        if (typeof initMoreSwatchButtons === 'function') {
          initMoreSwatchButtons(grid);
        }
      }

      if (window.lazyLoad?.update) {
        window.lazyLoad.update();
      }

      document.dispatchEvent(
        new CustomEvent('productsLoaded', {
          detail: { page, grid },
        })
      );
    }

    async loadNextPage(root) {
      if (root.dataset.loading === 'true') return;

      const nextUrl = root.dataset.nextUrl;
      if (!nextUrl) return;

      const grid = this.getGrid();
      if (!grid) return;

      root.dataset.loading = 'true';
      this.setPaginationLoading(root, true);

      try {
        const doc = await this.fetchDocument(nextUrl);
        let items = this.getProductsFromDocument(doc);
        const existingIds = this.getExistingProductIds(grid);

        items = items.filter((item) => {
          const id = item.dataset?.productId;
          return id && !existingIds.has(id);
        });

        if (!items.length) {
          root.removeAttribute('data-next-url');
          return;
        }

        const page = (parseInt(grid.dataset.currentPage, 10) || 1) + 1;
        const fragment = document.createDocumentFragment();

        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.page = String(page);
          clone.classList.add('scroll-trigger--cancel');
          fragment.appendChild(clone);
        });

        grid.appendChild(fragment);
        grid.dataset.currentPage = String(page);

        const nextScroller = doc.querySelector('infinite-scrolling');
        const newNext = nextScroller?.dataset?.nextUrl;
        if (newNext) {
          root.dataset.nextUrl = newNext;
          // Do NOT push ?page=N into the address bar. Infinite/load-more appends
          // cards client-side; refreshing a page=2 URL only shows that slice.
        } else {
          root.removeAttribute('data-next-url');
          const btn = root.querySelector('[data-infinite-scrolling]');
          if (btn) btn.style.display = 'none';
        }

        this.updatePaginationProgress(root, grid);
        this.reinitPaginatedItems(grid, page);

        if (root.dataset.nextUrl) {
          this.prefetch(root.dataset.nextUrl);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[CollectionController] pagination', error);
        }
        root.removeAttribute('data-next-url');
      } finally {
        root.dataset.loading = 'false';
        this.setPaginationLoading(root, false);
      }
    }

    bindPaginationRoot(root) {
      if (root.dataset.paginationBound === 'true') return;
      root.dataset.paginationBound = 'true';

      const btn = root.querySelector('[data-infinite-scrolling]');
      if (!btn) return;

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.loadNextPage(root);
      });

      // Desktop only: auto-load next page near footer. Mobile stays tap-to-load
      // so scroll/INP aren't competing with network + DOM appends.
      const mq = window.matchMedia(MOBILE_QUERY);
      const attachObserver = () => {
        if (mq.matches || !('IntersectionObserver' in window)) {
          if (root._paginationObserver) {
            root._paginationObserver.disconnect();
            delete root._paginationObserver;
          }
          return;
        }
        if (root._paginationObserver) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && root.dataset.nextUrl) {
                this.loadNextPage(root);
              }
            });
          },
          { rootMargin: '0px 0px 300px 0px', threshold: 0 }
        );
        observer.observe(btn);
        root._paginationObserver = observer;
      };

      attachObserver();
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', attachObserver);
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(attachObserver);
      }
    }

    bindPagination() {
      document.querySelectorAll('infinite-scrolling[data-next-url]').forEach((root) => {
        this.bindPaginationRoot(root);
      });
    }

    bindPrefetch() {
      // Desktop hover prefetch only — mobile touchstart prefetch competes with
      // LCP images and scroll while the user is still deciding.
      document.addEventListener(
        'mouseover',
        (event) => {
          if (window.matchMedia(MOBILE_QUERY).matches) return;
          const link = event.target.closest(
            '[data-ajax-filter], [data-collection-filter-link], .ff-dropdown-item'
          );
          if (!link?.href) return;
          this.prefetch(link.href);
        },
        { passive: true }
      );
    }

    bind() {
      window.addEventListener('popstate', () => this.onPopState());
      this.bindPagination();
      this.bindPrefetch();
      // Do NOT call prefetchVisibleFilters() on load — it fires ~10–20
      // concurrent full collection fetches while LCP images are still loading
      // (especially painful on wedding / multi-filter collections). Hover and
      // touch prefetch via bindPrefetch() is enough.
    }
  }

  window.CollectionController = CollectionController;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CollectionController.init());
  } else {
    CollectionController.init();
  }

  document.addEventListener('shopify:section:load', () => {
    CollectionController.init()?.bindPagination();
  });
})();
